import type { BoardPlacement } from "./Board.types";

export interface BoardCellEntry<TItem> {
    item: TItem;
    itemId: string;
    placement: BoardPlacement;
    placementKey: string;
}

export interface BoardCells<TItem> {
    entriesByCell: ReadonlyMap<string, ReadonlyArray<BoardCellEntry<TItem>>>;
    /** Unique items per column: copies in several lanes count once. */
    columnCounts: ReadonlyMap<string, number>;
    /** Cards per lane: every copy counts. */
    laneCounts: ReadonlyMap<string, number>;
}

interface BuildBoardCellsArgs<TItem> {
    items: readonly TItem[];
    getItemId: (item: TItem) => string;
    getPlacements: (item: TItem) => readonly BoardPlacement[];
    compareItems?: (first: TItem, second: TItem) => number;
}

export const getCellKey = ({ columnId, laneId }: BoardPlacement) =>
    `${columnId}::${laneId ?? ""}`;

/** How every placement key of `itemId` starts. */
export const getItemKeyPrefix = (itemId: string) => `${itemId}::`;

export const getPlacementKey = (itemId: string, placement: BoardPlacement) =>
    `${getItemKeyPrefix(itemId)}${getCellKey(placement)}`;

/** How every placement key in `placement`'s cell ends. */
export const getCellKeySuffix = (placement: BoardPlacement) =>
    getPlacementKey("", placement);

const EMPTY_ENTRIES: ReadonlyArray<BoardCellEntry<never>> = [];

export const getCellEntries = <TItem>(
    cells: BoardCells<TItem>,
    placement: BoardPlacement,
): ReadonlyArray<BoardCellEntry<TItem>> =>
    cells.entriesByCell.get(getCellKey(placement)) ?? EMPTY_ENTRIES;

/**
 * Groups items into cells keyed by placement, one entry per copy. Cells keep
 * the order of `items` unless `compareItems` is given.
 */
export const buildBoardCells = <TItem>({
    items,
    getItemId,
    getPlacements,
    compareItems,
}: BuildBoardCellsArgs<TItem>): BoardCells<TItem> => {
    const entriesByCell = new Map<string, Array<BoardCellEntry<TItem>>>();
    const itemIdsByColumn = new Map<string, Set<string>>();
    const laneCounts = new Map<string, number>();
    const placementKeys = new Set<string>();

    items.forEach((item) => {
        const itemId = getItemId(item);
        getPlacements(item).forEach((placement) => {
            const placementKey = getPlacementKey(itemId, placement);
            if (placementKeys.has(placementKey)) {
                return;
            }
            placementKeys.add(placementKey);

            const cellKey = getCellKey(placement);
            const entries = entriesByCell.get(cellKey) ?? [];
            entries.push({ item, itemId, placement, placementKey });
            entriesByCell.set(cellKey, entries);

            const columnItemIds =
                itemIdsByColumn.get(placement.columnId) ?? new Set<string>();
            columnItemIds.add(itemId);
            itemIdsByColumn.set(placement.columnId, columnItemIds);

            if (placement.laneId !== null) {
                laneCounts.set(
                    placement.laneId,
                    (laneCounts.get(placement.laneId) ?? 0) + 1,
                );
            }
        });
    });

    if (compareItems) {
        entriesByCell.forEach((entries) =>
            entries.sort((first, second) =>
                compareItems(first.item, second.item),
            ),
        );
    }

    const columnCounts = new Map(
        Array.from(itemIdsByColumn, ([columnId, itemIds]) => [
            columnId,
            itemIds.size,
        ]),
    );

    return { entriesByCell, columnCounts, laneCounts };
};
